import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatPaginator, MatPaginatorSelectConfig } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription, timer } from 'rxjs';
import { AdminService } from '../../../admin/admin.service';
import { ReportFieldOptionsService } from '../../../datatools-settings/report-field-options/report-field-options/report-field-options.service';
import { DataToolsLiteMessage } from '../../../shared/DataToolsLiteMessage';
import { DatatoolsLiteService } from '../../../shared/services/datatools-lite.service';
import { DuplikitMergeService } from '../../../shared/services/duplikit-merge.service';
import { MessageNotificationService } from '../../../shared/services/message-notification.service';
import { UserInterfaceService } from '../../../shared/services/user-interface.service';
import { UtilsService } from '../../../shared/services/utils.service';
import { DuplicateService } from '../../duplicate.service';
import { IDuplicatesDialogData } from '../duplicates-dialog/duplicates-dialog.component';
import { DuplikitReportField } from '../duplikit-report-field';
import { RecordDetails } from '../record-details';

@Component({
  selector: 'app-generic-duplicate-details',
  template: '<p>Not shown UI for inheritance purposes</p>',
  standalone: true,
  imports: [CommonModule]
})
export class GenericDuplicateDetailsComponent implements OnInit, OnDestroy {
  defaultFieldList: DuplikitReportField[] = [];
  readonly pageSizeSelectConfig: MatPaginatorSelectConfig = { panelClass: 'kyloe-paginator-select-panel' };
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Input() masterDuplicatesData: IDuplicatesDialogData;
  @Output() masterDuplicatesDataChange = new EventEmitter<IDuplicatesDialogData>();
  mergingEntity: string;
  isLoading = true;
  duplicateDetailsList = new MatTableDataSource<RecordDetails>();
  masterData: RecordDetails;
  fieldList: DuplikitReportField[];
  additionalReportFields: string[] = [];
  additionalReportFieldTooltip = 'This field can be configured in DataTools settings.';
  additionalReportFieldSubscription: Subscription;
  headerColumns: string[];
  masterRowHeaderColumns: string[];
  selectedDuplicates = new SelectionModel<RecordDetails>(true, []);
  timer: Subscription;

  constructor(
      private messageNotificationService: MessageNotificationService,
      private duplicateService: DuplicateService,
      private adminService: AdminService,
      private dataToolsLiteService: DatatoolsLiteService,
      protected reportFieldOptionsService: ReportFieldOptionsService,
      public utilsService: UtilsService,
      private duplikitMergeService: DuplikitMergeService,
      public userInterfaceService: UserInterfaceService,
  ) {
    this.fieldList = [...this.defaultFieldList];
  }

  ngOnInit(): void {
    this.mergingEntity = this.masterDuplicatesData.mergingEntity;
    this.initializeHeaderColumns();
    this.getAllDataForMasterAndDuplicates();
    this.getAdditionalReportFields();
  }

  private initializeHeaderColumns(): void {
    this.headerColumns = ['checkbox_n_master', 'id', 'name'].concat(this.fieldList.map(f => f.field));
    this.masterRowHeaderColumns = this.headerColumns.map(column => column + '-master');
  }


  toggleAll(): void {
    if (this.isAllSelected()) {
      this.selectedDuplicates.clear();
    } else {
      this.duplicateDetailsList.data.forEach(row => {
        if (row.bullhornID !== this.masterDuplicatesData.duplicateTableEntry.masterID) {
          this.selectedDuplicates.select(row);
        }
      });
    }
  }

  isAllSelected(): boolean {
    const numSelected = this.selectedDuplicates.selected.length;
    const numRows = this.duplicateDetailsList.data.length;
    return numSelected === numRows;
  }

  protected removeSelectedDuplicatesFromList(): void {
    const selectedIDs = new Set<number>(this.selectedDuplicates.selected.map(row => row.bullhornID));
    this.duplicateDetailsList.data = this.duplicateDetailsList.data.filter(duplicate => !selectedIDs.has(duplicate.bullhornID));
    this.masterDuplicatesData.duplicateTableEntry.duplicateIDs =
        new Set<number>(this.duplicateDetailsList.data.map(duplicate => duplicate.bullhornID));
    this.masterDuplicatesDataChange.emit(this.masterDuplicatesData);
    this.selectedDuplicates.clear();
  }

  getAllDataForMasterAndDuplicates(): void {
    this.duplicateService.getDuplicateDataForMaster(
        this.masterDuplicatesData.duplicateTableEntry.masterID, this.masterDuplicatesData.mergingEntity).subscribe((data) => {
      this.masterData = data.masterDetails;
      this.duplicateDetailsList = new MatTableDataSource<RecordDetails>(data.duplicateDetails);
      this.duplicateDetailsList.paginator = this.paginator;
    }, error => {
      console.error(error);
      this.messageNotificationService.error('Unable to Load Master Details', 'There was an error loading the details for this master record, please reload and try again. <br>');
    }, () => {
      this.isLoading = false;
    });
  }

  setNewMaster(newMaster: RecordDetails): void {
    this.isLoading = true;
    const oldMasterID: number = this.masterDuplicatesData.duplicateTableEntry.masterID;
    this.duplicateService.updateMasterIDEntity(this.mergingEntity, this.masterData.bullhornID, newMaster.bullhornID).subscribe((_) => {
      this.duplicateDetailsList.data.push(this.masterData);
      this.duplicateDetailsList.data.splice(this.duplicateDetailsList.data.indexOf(newMaster), 1);
      this.masterDuplicatesData.duplicateTableEntry.duplicateIDs.add(oldMasterID);
      this.masterDuplicatesData.duplicateTableEntry.masterID = newMaster.bullhornID;
      this.masterDuplicatesData.duplicateTableEntry.duplicateIDs.delete(newMaster.bullhornID);
      this.masterData = newMaster;
      this.masterDuplicatesDataChange.emit(this.masterDuplicatesData);
      this.selectedDuplicates.clear();
    }, error => {
      console.error('Could not set this record as Master.', error);
      this.messageNotificationService.error(
          'Unable to Update Master',
          'There was an issue setting this record as master. <br>' + error.message
      );
    }, () => {
      this.isLoading = false;
    });
  }

  markSelectedAsNotDuplicate(): void {
    if (this.adminService.isInLiteMode()) {
      this.dataToolsLiteService.triggerMessage$.next(DataToolsLiteMessage.DUPLIKIT_MARK_NON_DUPE);
      return;
    }
    this.isLoading = true;
    const nonDuplicateIDs: number[] = this.selectedDuplicates.selected.map(row => row.bullhornID);
    // TODO:
    //  - Tidy this up. This double step of checking the call was successful seems really jank
    //  - why is entityNonDupeByIDs(..) returning a boolean for us to check?
    //  - If the call fails, just hand back an error instead of a boolean
    this.duplicateService.markAsNotDuplicatesByID(
        this.mergingEntity,
        this.masterDuplicatesData.duplicateTableEntry.masterID,
        nonDuplicateIDs
    ).subscribe((success: boolean) => {
      if (!success) {
        this.messageNotificationService.error(
            `Unable to Disassociate Records`,
            `An error occurred disassociating [${ nonDuplicateIDs.toString() }] as ${ nonDuplicateIDs.length === 1 ? 'a duplicate' : 'duplicates' } of #${ this.masterDuplicatesData.duplicateTableEntry.masterID }. Please try again.`
        );
      } else {
        this.messageNotificationService.success('Successfully Disassociated Records', `Successfully disassociated [${ nonDuplicateIDs.toString() }] as ${ nonDuplicateIDs.length === 1 ? 'a duplicate' : 'duplicates' } of #${ this.masterDuplicatesData.duplicateTableEntry.masterID }.`);
        this.removeSelectedDuplicatesFromList();
      }
    }, (error) => {
      console.error(error);
      this.messageNotificationService.error(
          `Unable to Disassociate Duplicate${ nonDuplicateIDs.length === 1 ? '' : 's' }`,
          `An error occurred disassociating [${ nonDuplicateIDs.toString() }] as ${ nonDuplicateIDs.length === 1 ? 'a duplicate' : 'duplicates' } of #${ this.masterDuplicatesData.duplicateTableEntry.masterID }.<br>${ error.message }`
      );
    }, () => {
      this.isLoading = false;
    });
  }

  mergeSelected(): void {
    if (this.adminService.isInLiteMode()) {
      this.dataToolsLiteService.triggerMessage$.next(DataToolsLiteMessage.DUPLIKIT_MULTIPLE_MERGE);
      return;
    }
    this.isLoading = true;
    const duplicateIDs: number[] = this.selectedDuplicates.selected.map(row => row.bullhornID);
    this.duplikitMergeService.setEntityIsMerging(this.mergingEntity);
    this.duplicateService.mergeSelectedEntity(this.mergingEntity, this.masterData.bullhornID, duplicateIDs).subscribe((success) => {
      if (!success) {
        this.messageNotificationService.error('Merge in progress', 'A merge is already in progress. Please wait.');
      } else {
        this.messageNotificationService.info('Starting Merge', `Merge in progress for ${ this.utilsService.getProperEntityName(this.mergingEntity) } #${ this.masterData.bullhornID } with duplicates [${ duplicateIDs.join(', ') }]`);
        this.pollCompletion();
        this.removeSelectedDuplicatesFromList();
      }
    }, (error) => {
      console.error(error);
      this.messageNotificationService.error('Unable to Merge Records', `An error occurred merging records masterId #${ this.masterData.bullhornID }, duplicates [${ duplicateIDs.join(', ') }]: <br>` + error.message);
    }, () => {
      this.isLoading = false;
    });
  }

  private pollCompletion(): void {
    this.duplikitMergeService.runMergeCheck();
    this.timer = timer(2000, 3000).subscribe(() => {
      if (!this.duplikitMergeService.getEntityThatIsMerging()) {
        this.messageNotificationService.success('Merge complete', 'Merge has completed, check History page for results.');
        this.userInterfaceService.duplikit.mergeButtonsDisabled = false;
        this.duplicateService.refreshState(this.mergingEntity);
        this.timer.unsubscribe();
      }
    });
  }

  private getAdditionalReportFields(): void {
    this.additionalReportFieldSubscription = this.reportFieldOptionsService.getSelectedReportFields(this.mergingEntity)
      .subscribe((response) => {
        if (response) {
          this.additionalReportFields = response;
          this.fieldList = [...this.defaultFieldList];
          response.forEach((field) => this.fieldList.push({ field: field, label: field }));
          this.initializeHeaderColumns();
        }
      });
  }

  ngOnDestroy(): void {
    this.additionalReportFieldSubscription?.unsubscribe();
  }
}
